import { createApi } from '@reduxjs/toolkit/query/react';
import { HandoverTask, HandoverTaskResponse } from '@handover/shared-types';
import { HANDOVER_TASK_END_POINTS, METHOD } from '../../constants/constant';
import { customHandoverBaseQuery } from '../customBaseQuery';

const normalizeHandoverTaskResponse = (response: any): HandoverTaskResponse => {
  const taskDoc = response?.handoverTaskDoc || {};
  const tasks = Array.isArray(taskDoc?.tasks) ? taskDoc.tasks : [];

  return {
    ...response,
    handoverTaskDoc: {
      ...taskDoc,
      tasks: tasks,
    },
  };
};

export const handoverTaskApi = createApi({
  reducerPath: 'handoverTaskApi',
  baseQuery: customHandoverBaseQuery,
  keepUnusedDataFor: 300, // Keep data in cache for 5 minutes
  refetchOnMountOrArgChange: 30, // Refetch if data is older than 30 seconds
  refetchOnFocus: false, // Disable refetch on focus to reduce API calls
  refetchOnReconnect: true,
  tagTypes: ['Elements'],
  endpoints: builder => ({
    // Fetch the Handover Task document by Handover ID
    getHandoverTask: builder.query<HandoverTaskResponse, string>({
      query: handoverId => ({
        url: HANDOVER_TASK_END_POINTS.gethandovertaskdocbyhandoverid.replace(':handoverId', handoverId),
        method: METHOD.GET,
      }),
      transformResponse: normalizeHandoverTaskResponse,
      providesTags: ['Elements'],
      keepUnusedDataFor: 300, // Keep data for 5 minutes
    }),
    // Update or create a new Handover Task
    updateHandoverTask: builder.mutation<
      HandoverTaskResponse,
      { handoverId: string; taskData: HandoverTask }
    >({
      query: ({ handoverId, taskData }) => ({
        url: HANDOVER_TASK_END_POINTS.updatehandovertask.replace(':handoverId', handoverId),
        method: METHOD.PUT,
        body: taskData,
      }),
      transformResponse: normalizeHandoverTaskResponse,
      invalidatesTags: ['Elements'],
    }),
    deleteHandoverTask: builder.mutation<HandoverTaskResponse, { handoverId: string; taskId: string }>({
      query: ({ handoverId, taskId }) => ({
        url: HANDOVER_TASK_END_POINTS.deletehandovertask
          .replace(':handoverId', handoverId)
          .replace(':taskId', taskId),
        method: METHOD.DELETE,
      }),
      transformResponse: normalizeHandoverTaskResponse,
      invalidatesTags: ['Elements'],
    }),
    // Upload file and create/update Handover Task
    uploadHandoverTaskFile: builder.mutation<
      HandoverTaskResponse,
      {
        handoverId: string;
        file: File;
        id?: string;
        type: string;
        title?: string;
        content?: string;
        section?: HandoverTask['section'];
      }
    >({
      query: ({ handoverId, file, id, type, title, content, section }) => {
        const formData = new FormData();
        formData.append('file', file);

        const params = new URLSearchParams();
        if (id) params.append('id', id);
        if (type) params.append('type', type);
        if (title) params.append('title', title);
        if (content) params.append('content', content);
        if (section) params.append('section', section);

        return {
          url: `${HANDOVER_TASK_END_POINTS.uploadhandovertaskfile.replace(':handoverId', handoverId)}?${params.toString()}`,
          method: METHOD.POST,
          body: formData,
        };
      },
      invalidatesTags: ['Elements'],
    }),
  }),
});

export const {
  useDeleteHandoverTaskMutation,
  useGetHandoverTaskQuery,
  useUpdateHandoverTaskMutation,
  useUploadHandoverTaskFileMutation,
} = handoverTaskApi;
